// features/companies/api.ts — typed fetch for the per-company document
// feed. Hits `GET /companies/:cnpj/documents` on the backend, which returns
// every filing (any of the 7 types) recorded for one CNPJ, sorted by delivery
// date descending. Shape mirrors DashboardDocument so the page can reuse the
// existing presentation patterns.
//
// Omitting `year` (or passing it as 0) means "every available year" — the
// backend defaults to all years with coverage when the query param is
// missing. The backend response carries the resolved `years` array.
import { toDigitsCnpj } from "@shared/utils/cnpj";

import type { DashboardDocument } from "@/features/dashboard/api";
import { searchAll } from "@/features/search/api";
import { apiRequest } from "@/lib/api-client";
import { v2Path } from "@/lib/v2-path";

/** B3 trading code: a 4-character root carrying at least one letter, then 1–2
 *  class digits, then an optional `F` (fractional) or `B` (Bovespa Mais)
 *  suffix. The root allows digits — `B3SA3` (B3 itself), `B1003`, `SC303` and
 *  the BDR shapes all have one. Nothing 8+ characters matches, so a CNPJ in
 *  either format falls through to the canonical path with no lookup, and the
 *  FCA sentinels the backfill worker copies into `companies.tickers`
 *  (`0000`, `000000`, `999999`, CVM codes like `021725`) are rejected by the
 *  letter requirement. */
export const TICKER_RE = /^(?=.*[A-Za-z])[A-Za-z0-9]{4}\d{1,2}[FBfb]?$/;

/** CNPJ behind a trading code, or `undefined` when no company carries it.
 *
 *  ponytail: reuses the ⌘K search endpoint rather than adding a ticker→CNPJ
 *  route — it already matches `companies.tickers` and ranks an exact hit
 *  above every fuzzy tier. Retired codes (`former_tickers`) resolve to a
 *  fuzzy row whose `tickers` lack the code, so they come back undefined. */
export async function cnpjForTicker(
  ticker: string,
  opts: { signal?: AbortSignal } = {},
): Promise<string | undefined> {
  const upper = ticker.toUpperCase();
  const { companies } = await searchAll(upper, opts);
  return companies.find((c) => c.tickers?.includes(upper))?.cnpj;
}

export type CompanyDocumentsResponse = {
  data: DashboardDocument[];
  count: number;
  /** Years the backend actually queried — useful for the all-years header. */
  years: number[];
  cnpj: string;
  types: Record<string, number>;
};

export function getCompanyDocuments(
  cnpj: string,
  opts: { year?: number; signal?: AbortSignal } = {},
): Promise<CompanyDocumentsResponse> {
  const digits = toDigitsCnpj(cnpj) ?? cnpj;
  // v2: POST + body instead of GET + query string. The search-POST body
  // carries the year as `year`, the cnpj as `filters.cnpj`, and the
  // limit/offset as siblings. The v1 GET stays live during the cutover
  // window (AGENTS.md §1.5).
  return apiRequest<CompanyDocumentsResponse>("/v2/companies-documents/search", {
    method: "POST",
    body: {
      year: opts.year !== undefined && opts.year > 0 ? opts.year : undefined,
      filters: { cnpj: digits },
    },
    signal: opts.signal,
  });
}

// ponytail: company profile. The backend endpoint
// `GET /companies/:cnpj/profile` lazy-populates from the FCA geral + endereco
// parquets on first hit and persists the result on the `companies` row. The
// `profile` is `null` when the parquet has no filings for the CNPJ — the
// frontend renders an empty state instead of treating it as a 404.
export interface CompanyAddress {
  street: string | null;
  complement: string | null;
  neighborhood: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  cep: string | null;
}

export interface CompanyProfile {
  companyName: string | null;
  cnpj: string;
  cvmCode: string | null;
  sector: string | null;
  activityDescription: string | null;
  website: string | null;
  headquarters: CompanyAddress | null;
  mailingAddress: CompanyAddress | null;
  phone: string | null;
  email: string | null;
  constitutionDate: string | null;
  registrationStatus: string | null;
  controlType: string | null;
  countryOfOrigin: string | null;
  sourceYear: number;
  populatedAt: string;
  /** Primary trading code, or null when the CNPJ has no listed share.
   *
   *  ponytail: this used to carry a `price`/`priceDate` pair joined from the
   *  VLMO insider-trading form. Removed — the join keyed on a column the VLMO
   *  parquet has never had, so the value was always null, and an insider
   *  transaction price is not a quote anyway. Live prices come from the
   *  fundamentals endpoint (`quotes[]`), which reads Google Finance. */
  tickerSummary: {
    ticker: string;
    sourceYear: number;
    source: string;
    securityClass: string | null;
  } | null;
  /** Every active CVM ticker for this CNPJ. Backfilled by
   *  `apps/backend/src/worker/backfill-company-names.ts` into the
   *  `companies.tickers` Postgres column and surfaced through the
   *  profile payload. Empty array before the worker runs for a new CNPJ
   *  or when no FCA valor_mobiliario row exists. */
  tickers: string[];
  /** Every active trading code for this CNPJ with its security class, so the
   *  profile block can render them side by side. Empty when the CNPJ has no
   *  listed share. */
  tickerSummaries: {
    ticker: string;
    sourceYear: number;
    source: string;
    securityClass: string | null;
  }[];
}

export type CompanyProfileResponse = {
  data: { cnpj: string; profile: CompanyProfile | null };
};

export function getCompanyProfile(
  cnpj: string,
  opts: { signal?: AbortSignal } = {},
): Promise<CompanyProfileResponse> {
  const digits = toDigitsCnpj(cnpj) ?? cnpj;
  return apiRequest<CompanyProfileResponse>(`/v2/companies/${encodeURIComponent(digits)}/profile`, {
    signal: opts.signal,
  });
}

// ponytail: Fundamentus-style indicator pack. Everything but the share price
// is computed from the ITR + DFP parquets on the backend
// (`analytics/fundamentals.ts`); the price is a live Google Finance quote.
// `data` is null when the CNPJ has no statements in the lake — the card
// renders nothing rather than an error, since plenty of registered issuers
// (funds, recently listed companies) have never filed an ITR or DFP.
export interface FundamentalsPeriod {
  start: string | null;
  end: string | null;
  revenue: number | null;
  grossProfit: number | null;
  ebit: number | null;
  ebitda: number | null;
  depreciation: number | null;
  netIncome: number | null;
  dividendsPaid: number | null;
}

export interface FundamentalsQuote {
  ticker: string;
  price: number | null;
  currency: string | null;
  asOf: string | null;
  marketCap: number | null;
  enterpriseValue: number | null;
  priceToEarnings: number | null;
  priceToBook: number | null;
  priceToSales: number | null;
  priceToEbit: number | null;
  priceToAssets: number | null;
  priceToWorkingCapital: number | null;
  priceToNetCurrentAssets: number | null;
  dividendYield: number | null;
  evToEbitda: number | null;
  evToEbit: number | null;
}

/** Income lines of the CVM financial-institution chart of accounts. A bank
 *  has no EBIT and no gross profit; these replace them. */
export interface BankIncome {
  interestIncome: number | null;
  interestExpense: number | null;
  netInterestIncome: number | null;
  creditCost: number | null;
  feeIncome: number | null;
  personnelExpense: number | null;
  adminExpense: number | null;
  pretaxResult: number | null;
  netIncome: number | null;
}

export interface BankFundamentals {
  income: { ttm: BankIncome; quarter: BankIncome | null };
  balance: {
    cash: number | null;
    financialAssets: number | null;
    creditPortfolio: number | null;
    loanLossAllowance: number | null;
    deposits: number | null;
    openMarketFunding: number | null;
  };
  ratios: {
    efficiency: number | null;
    netInterestMarginOnAssets: number | null;
    creditCost: number | null;
    coverage: number | null;
    loanToDeposits: number | null;
    creditToAssets: number | null;
    leverage: number | null;
  };
}

export interface Fundamentals {
  cnpj: string;
  shares: {
    total: number | null;
    ordinary: number | null;
    preferred: number | null;
    treasury: number | null;
    scaleFactor: number;
    referenceDate: string | null;
  };
  balance: {
    referenceDate: string | null;
    totalAssets: number | null;
    currentAssets: number | null;
    cash: number | null;
    currentLiabilities: number | null;
    nonCurrentLiabilities: number | null;
    totalLiabilities: number | null;
    suppliers: number | null;
    grossDebt: number | null;
    netDebt: number | null;
    equity: number | null;
  };
  income: {
    ttm: FundamentalsPeriod & { basis: "ttm" | "annual" };
    quarter: FundamentalsPeriod | null;
    /** Net revenue per closed fiscal year (DFP), oldest first — the series
     *  `ratios.revenueCagr5y` is computed over. */
    annualRevenues: { year: string; revenue: number | null }[];
  };
  ratios: {
    grossMargin: number | null;
    ebitMargin: number | null;
    netMargin: number | null;
    roe: number | null;
    roa: number | null;
    roic: number | null;
    ebitOverAssets: number | null;
    currentRatio: number | null;
    debtToEquity: number | null;
    netDebtToEbitda: number | null;
    assetTurnover: number | null;
    revenueCagr5y: number | null;
    lpa: number | null;
    vpa: number | null;
  };
  quotes: FundamentalsQuote[];
  /** Non-null only when `meta.financialPlan` — the bank indicator set that
   *  replaces the general one. */
  bank: BankFundamentals | null;
  meta: {
    lastBalanceFiling: "itr" | "dfp" | null;
    lastBalanceDate: string | null;
    lastAnnualDate: string | null;
    scope: "con" | "ind" | null;
    /** Issuer files under CVM's financial-institution chart of accounts, where
     *  EBIT and the current/non-current split don't exist — the indicators
     *  built on them come back null. */
    financialPlan: boolean;
  };
}

export function getCompanyFundamentals(
  cnpj: string,
  opts: { signal?: AbortSignal } = {},
): Promise<{ data: Fundamentals | null }> {
  const digits = toDigitsCnpj(cnpj) ?? cnpj;
  return apiRequest<{ data: Fundamentals | null }>(
    `/v2/fundamentals/cnpj/${encodeURIComponent(digits)}`,
    { signal: opts.signal },
  );
}

/** Batch lookup of every active ticker per CNPJ. Backend source:
 *  `companies.tickers` (Postgres). Returns a `cnpj → tickers` map —
 *  CNPJs without tickers are omitted from the map. */
export function getCompaniesTickers(
  cnpjs: string[],
  opts: { signal?: AbortSignal } = {},
): Promise<{ data: Record<string, string[]> }> {
  const payload: { cnpjs: string[] } = { cnpjs: [...cnpjs] };
  // ponytail: the v2 tickers endpoint is a fixed POST under
  // `/v2/companies-tickers/list` — body carries the CNPJ batch. `v2Path`
  // doesn't append a `list: true` segment (it would treat `list` as an
  // arbitrary key/value), so build the path directly.
  return apiRequest<{ data: Record<string, string[]> }>(v2Path("/v2/companies-tickers/list"), {
    method: "POST",
    body: payload,
    signal: opts.signal,
  });
}
